import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, Search, Check } from "lucide-react";

/**
 * Compact GHL-style Sort popover.
 * • `fields` — array of { key, label } to sort by
 * • `field` / `dir` — controlled state (`asc` | `desc`)
 * • `onChange(field, dir)` — called on any change
 * • `testid` — prefix for data-testid (default "sort")
 * • `defaultField` — reset target for the "Reset" link
 * • `defaultDir` — reset target for the "Reset" link
 */
export default function SortPopover({
  fields,
  field,
  dir,
  onChange,
  testid = "sort",
  defaultField,
  defaultDir = "desc",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const filtered = fields.filter((f) => t(f.label).toLowerCase().includes(query.toLowerCase()));
  const current = fields.find((f) => f.key === field);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        data-testid={`${testid}-btn`}
        className="sp-btn-ghost inline-flex items-center gap-1.5 text-sm">
        <ArrowUpDown className="h-4 w-4" />
        <span>{t("sort.button")}</span>
        {current && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] text-[11px] font-bold">
            {t(current.label)}
            {dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 z-30 w-72 rounded-2xl border border-[color:var(--sp-border)] bg-white shadow-2xl p-3"
          data-testid={`${testid}-popover`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] font-bold text-[color:var(--sp-text-secondary)] uppercase tracking-wide flex-1">{t("sort.by")}</div>
            <button onClick={() => { setQuery(""); onChange(defaultField || fields[0]?.key, defaultDir); }}
              className="text-[11px] text-red-600 hover:underline">{t("common.reset")}</button>
          </div>
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("sort.selectField")}
              data-testid={`${testid}-search`}
              className="w-full pl-8 pr-2 py-1.5 rounded-lg border border-[color:var(--sp-border)] text-sm outline-none focus:border-[color:var(--sp-brand)]" />
          </div>
          <div className="flex items-center gap-1 mb-2">
            <button onClick={() => onChange(field, "asc")}
              data-testid={`${testid}-dir-asc`}
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold border transition-colors ${dir === "asc" ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "border-[color:var(--sp-border)]"}`}>
              <ArrowUp className="h-3 w-3" /> {t("sort.asc")}
            </button>
            <button onClick={() => onChange(field, "desc")}
              data-testid={`${testid}-dir-desc`}
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold border transition-colors ${dir === "desc" ? "border-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "border-[color:var(--sp-border)]"}`}>
              <ArrowDown className="h-3 w-3" /> {t("sort.desc")}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto -mx-1">
            {filtered.map((f) => (
              <button key={f.key} onClick={() => { onChange(f.key, dir); setOpen(false); }}
                data-testid={`${testid}-field-${f.key}`}
                className={`w-full text-left px-3 py-1.5 rounded-lg text-sm hover:bg-[color:var(--sp-subtle)] transition-colors ${field === f.key ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] font-bold" : ""}`}>
                {t(f.label)}
                {field === f.key && <Check className="inline h-3 w-3 ml-2" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-4 text-xs text-[color:var(--sp-text-muted)]">{t("sort.noField")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
